// Every offer waiting on the local house, read off the snapshot — the one list
// both the toasts and the Diplomacy tab draw from.
//
// A toast is only a notification: it fades after a few seconds so it does not
// cover the board, but the offer is still on the table until the proposer
// withdraws it, the sim lets it lapse, or the match ends — and while it is, it
// is answerable from the Diplomacy tab. Both views read this, so an offer that
// leaves the table leaves both at once.
import type { PendingMerchantOffer } from '../sea/pendingOffer.js';
import { type Diplomacy, type Relation, offersTo } from './diplomacy.js';
import { type Dynasty, type MarriageProposal, proposalTo } from './dynasty.js';

export type PendingOffer =
  | {
      readonly kind: 'treaty';
      readonly key: string;
      readonly from: number;
      readonly state: Relation;
      readonly expiresTick: number;
    }
  | {
      readonly kind: 'marriage';
      readonly key: string;
      readonly from: number;
      readonly offer: MarriageProposal;
    }
  | { readonly kind: 'merchant'; readonly key: string; readonly merchant: PendingMerchantOffer };

/** A re-made offer carries a new expiry, so it is a new key — and a new toast. */
export const treatyOfferKey = (o: {
  readonly from: number;
  readonly state: Relation;
  readonly expiresTick: number;
}): string => `treaty:${o.from}:${o.state}:${o.expiresTick}`;

export const marriageOfferKey = (o: {
  readonly from: number;
  readonly expiresTick: number;
}): string => `marriage:${o.from}:${o.expiresTick}`;

export const merchantOfferKey = (m: PendingMerchantOffer): string =>
  `merchant:${m.cityId}:${m.offer.vesselId}:${m.offer.untilTick}`;

/** The offers on the table for `me`, oldest rung first: treaties, marriage, merchant. */
export function pendingOffers(
  diplomacy: Diplomacy | null,
  dynasty: Dynasty | null,
  merchant: PendingMerchantOffer | null,
  me: number,
): readonly PendingOffer[] {
  const out: PendingOffer[] = [];
  for (const o of offersTo(diplomacy, me)) {
    out.push({
      kind: 'treaty',
      key: treatyOfferKey(o),
      from: o.from,
      state: o.state,
      expiresTick: o.expiresTick,
    });
  }
  const marriage = dynasty ? proposalTo(dynasty, me) : null;
  if (marriage) {
    out.push({
      kind: 'marriage',
      key: marriageOfferKey(marriage),
      from: marriage.from,
      offer: marriage,
    });
  }
  if (merchant) out.push({ kind: 'merchant', key: merchantOfferKey(merchant), merchant });
  return out;
}
